import React, { useState, useEffect, useRef } from 'react';
import UserProfile from '../common/UserProfile';

const AdminView = () => {
  const [activeTab, setActiveTab] = useState('dashboard');
  const [loading, setLoading] = useState(false);
  const [showProfile, setShowProfile] = useState(false);
  
  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || window.location.origin;
  
  // Dashboard data
  const [stats, setStats] = useState({
    totalBookings: 0, completedBookings: 0,
    activeDrivers: 0, totalDrivers: 0,
    totalRevenue: 0, pendingInvoices: 0
  });
  
  const [earnings, setEarnings] = useState({
    total_revenue: 0, company_commission: 0,
    driver_earnings: 0, total_trips: 0, commission_percentage: 30
  });

  const [adminTimeRange, setAdminTimeRange] = useState('all_time');
  
  // Driver management
  const [drivers, setDrivers] = useState([]);
  const [showAddDriver, setShowAddDriver] = useState(false);
  const [newDriver, setNewDriver] = useState({
    name: '', email: '', phone: '', license: '',
    vehicleMake: '', vehicleModel: '', vehicleYear: '',
    vehicleColor: '', vehiclePlate: ''
  });
  
  // Booking management
  const [bookings, setBookings] = useState([]);
  const [bookingFilter, setBookingFilter] = useState('all');
  const [bookingSortOrder, setBookingSortOrder] = useState('upcoming'); // 'upcoming' | 'newest'

  // Driver applications
  const [driverApplications, setDriverApplications] = useState([]);

  // Notifications
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [showNotifications, setShowNotifications] = useState(false);
  const notifRef = useRef(null);
  
  // Trip management
  const [pendingTrips, setPendingTrips] = useState([]);
  

  useEffect(() => {
    loadAdminData();
  }, [adminTimeRange]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const interval = setInterval(() => {
      loadAdminData();
      loadNotifications();
    }, 30000);
    return () => clearInterval(interval);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    loadAdminData();
    loadNotifications();
  }, [activeTab, adminTimeRange]); // eslint-disable-line react-hooks/exhaustive-deps

  // Close notification dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (notifRef.current && !notifRef.current.contains(e.target)) {
        setShowNotifications(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const loadNotifications = async () => {
    try {
      const [notifsRes, countRes] = await Promise.all([
        fetch(`${BACKEND_URL}/api/admin/notifications`),
        fetch(`${BACKEND_URL}/api/admin/notifications/unread-count`)
      ]);
      if (notifsRes.ok) setNotifications(await notifsRes.json());
      if (countRes.ok) { const d = await countRes.json(); setUnreadCount(d.count); }
    } catch { /* ignore */ }
  };

  const markNotificationRead = async (id) => {
    await fetch(`${BACKEND_URL}/api/admin/notifications/${id}/read`, { method: 'PUT' });
    loadNotifications();
  };

  const markAllRead = async () => {
    await fetch(`${BACKEND_URL}/api/admin/notifications/mark-all-read`, { method: 'PUT' });
    loadNotifications();
  };

  const loadDriverApplications = async () => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/admin/driver-applications`);
      if (res.ok) setDriverApplications(await res.json());
    } catch { /* ignore */ }
  };

  const handleApplicationAction = async (appId, action) => {
    try {
      await fetch(`${BACKEND_URL}/api/admin/driver-applications/${appId}/${action}`, { method: 'PUT' });
      loadDriverApplications();
      loadNotifications();
    } catch { /* ignore */ }
  };

  const handleDeactivateDriver = async (driverId, driverName) => {
    if (!window.confirm(`Deactivate driver ${driverName}? They will not be able to log in until reactivated.`)) return;
    try {
      const res = await fetch(`${BACKEND_URL}/api/admin/drivers/${driverId}/deactivate`, { method: 'PUT' });
      if (res.ok) { alert(`${driverName} has been deactivated.`); loadAdminData(); }
    } catch { alert('Failed to deactivate driver'); }
  };

  const handleActivateDriver = async (driverId, driverName) => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/admin/drivers/${driverId}/activate`, { method: 'PUT' });
      if (res.ok) { alert(`${driverName} has been reactivated.`); loadAdminData(); }
    } catch { alert('Failed to activate driver'); }
  };

  const handleSendNoDriverMessage = async (bookingId, passengerName) => {
    const msg = window.prompt(
      `Send "no driver available" message to ${passengerName}:`,
      "We're sorry, no drivers are available for your booking at this time. Our team will contact you shortly to reschedule."
    );
    if (!msg) return;
    try {
      const res = await fetch(`${BACKEND_URL}/api/bookings/${bookingId}/no-driver-message`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ message: msg })
      });
      if (res.ok) {
        alert('Rider has been notified.');
        loadAdminData();
      } else {
        alert('Failed to send notification');
      }
    } catch { alert('Network error'); }
  };

  const loadAdminData = async () => {
    try {
      // Load dashboard stats with time range
      const statsResponse = await fetch(`${BACKEND_URL}/api/admin/stats?time_range=${adminTimeRange}`);
      if (statsResponse.ok) {
        const statsData = await statsResponse.json();
        setStats(statsData);
      }
      
      // Load earnings summary with time range
      const earningsResponse = await fetch(`${BACKEND_URL}/api/admin/earnings-summary?time_range=${adminTimeRange}`);
      if (earningsResponse.ok) {
        const earningsData = await earningsResponse.json();
        setEarnings(earningsData.summary);
      }
      
      // Load drivers
      const driversResponse = await fetch(`${BACKEND_URL}/api/drivers`);
      if (driversResponse.ok) {
        const driversData = await driversResponse.json();
        setDrivers(driversData);
      }
      
      // Load bookings
      const bookingsResponse = await fetch(`${BACKEND_URL}/api/bookings`);
      if (bookingsResponse.ok) {
        const bookingsData = await bookingsResponse.json();
        setBookings(bookingsData);
      }
      
      // Load trip submissions (new feature for driver trip approvals)  
      const submissionsResponse = await fetch(`${BACKEND_URL}/api/admin/trip-submissions`);
      if (submissionsResponse.ok) {
        await submissionsResponse.json();
      }
      
      // Load pending trips (new feature for driver trip approvals)
      const tripsResponse = await fetch(`${BACKEND_URL}/api/admin/pending-trips`);
      if (tripsResponse.ok) {
        const tripsData = await tripsResponse.json();
        setPendingTrips(tripsData);
      }
    } catch (error) {
      console.error('Failed to load admin data:', error);
    }
  };

  const handleCreateDriver = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      const driverData = {
        email: newDriver.email,
        name: newDriver.name,
        phone: newDriver.phone,
        license_number: newDriver.license,
        vehicle_make: newDriver.vehicleMake,
        vehicle_model: newDriver.vehicleModel,
        vehicle_year: parseInt(newDriver.vehicleYear) || new Date().getFullYear(),
        vehicle_color: newDriver.vehicleColor,
        vehicle_plate: newDriver.vehiclePlate
      };
      
      const response = await fetch(`${BACKEND_URL}/api/admin/drivers`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(driverData)
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to create driver');
      }
      
      const result = await response.json();
      
      // Show success message with temporary password
      window.alert(`Driver created successfully!\n\nLogin Details:\nEmail: ${newDriver.email}\nTemporary Password: ${result.temporary_password}\n\nPlease share these credentials with the driver.`);
      
      // Reset form and reload data
      setNewDriver({
        name: '', email: '', phone: '', license: '',
        vehicleMake: '', vehicleModel: '', vehicleYear: '',
        vehicleColor: '', vehiclePlate: ''
      });
      setShowAddDriver(false);
      loadAdminData();
      
    } catch (error) {
      window.alert(`Error creating driver: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleApproveTrip = async (tripId) => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/admin/trips/${tripId}/approve`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' }
      });
      
      if (response.ok) {
        setPendingTrips(prev => prev.filter(trip => trip.id !== tripId));
        loadAdminData();
        alert('Trip approved successfully!');
      }
    } catch (error) {
      console.error('Failed to approve trip:', error);
      alert('Failed to approve trip');
    }
  };

  const handleRejectTrip = async (tripId, reason) => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/admin/trips/${tripId}/reject`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ reason })
      });
      
      if (response.ok) {
        setPendingTrips(prev => prev.filter(trip => trip.id !== tripId));
        alert('Trip rejected');
      }
    } catch (error) {
      console.error('Failed to reject trip:', error);
      alert('Failed to reject trip');
    }
  };

  // Booking action handlers
  const handleAssignDriver = async (bookingId) => {
    // For now, we'll show available drivers and let admin select
    if (drivers.length === 0) {
      window.alert('No drivers available. Please add drivers first.');
      return;
    }
    
    const availableDrivers = drivers.filter(driver => driver.is_available);
    if (availableDrivers.length === 0) {
      window.alert('No available drivers at the moment.');
      return;
    }
    
    // Simple driver selection for now
    const driverNames = availableDrivers.map(d => `${d.name} (${d.vehicle_make} ${d.vehicle_model})`);
    const selectedIndex = window.prompt(`Select driver:\n${driverNames.map((name, index) => `${index + 1}. ${name}`).join('\n')}\n\nEnter number (1-${driverNames.length}):`);
    
    if (selectedIndex && selectedIndex >= 1 && selectedIndex <= availableDrivers.length) {
      const selectedDriver = availableDrivers[selectedIndex - 1];
      
      try {
        const response = await fetch(`${BACKEND_URL}/api/bookings/${bookingId}/assign-driver?driver_id=${selectedDriver.id}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' }
        });
        
        if (response.ok) {
          loadAdminData();
          window.alert(`Driver ${selectedDriver.name} assigned successfully!`);
        } else {
          window.alert('Failed to assign driver');
        }
      } catch (error) {
        console.error('Failed to assign driver:', error);
        window.alert('Failed to assign driver');
      }
    }
  };

  const handleCancelBooking = async (bookingId) => {
    if (window.confirm('Are you sure you want to cancel this booking?')) {
      try {
        const response = await fetch(`${BACKEND_URL}/api/bookings/${bookingId}/status?status=cancelled`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' }
        });
        
        if (response.ok) {
          loadAdminData();
          window.alert('Booking cancelled successfully');
        } else {
          window.alert('Failed to cancel booking');
        }
      } catch (error) {
        console.error('Failed to cancel booking:', error);
        window.alert('Failed to cancel booking');
      }
    }
  };

  const handleStartTrip = async (bookingId) => {
    if (window.confirm('Mark this trip as started?')) {
      try {
        const response = await fetch(`${BACKEND_URL}/api/bookings/${bookingId}/status?status=in_progress`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' }
        });
        
        if (response.ok) {
          loadAdminData();
          window.alert('Trip started successfully');
        } else {
          window.alert('Failed to start trip');
        }
      } catch (error) {
        console.error('Failed to start trip:', error);
        window.alert('Failed to start trip');
      }
    }
  };

  const handleReassignDriver = async (bookingId) => {
    // Similar to assign driver but for reassignment
    handleAssignDriver(bookingId);
  };

  const handleCompleteTrip = async (bookingId) => {
    if (window.confirm('Mark this trip as completed?')) {
      try {
        const response = await fetch(`${BACKEND_URL}/api/bookings/${bookingId}/status?status=completed`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' }
        });
        
        if (response.ok) {
          loadAdminData();
          window.alert('Trip completed successfully');
        } else {
          window.alert('Failed to complete trip');
        }
      } catch (error) {
        console.error('Failed to complete trip:', error);
        window.alert('Failed to complete trip');
      }
    }
  };

  const handleViewBookingDetails = (bookingId) => {
    const booking = bookings.find(b => b.id === bookingId);
    if (booking) {
      const details = `
Booking Details:
================
ID: ${booking.id}
Passenger: ${booking.passengerName}
Phone: ${booking.passengerPhone}
Email: ${booking.passengerEmail}

Trip Details:
From: ${booking.pickupAddress}
To: ${booking.destinationAddress}
Date: ${booking.scheduledDate} at ${booking.scheduledTime}
Companion Time: ${booking.companionMinutes} minutes

Special Requirements: ${booking.specialRequirements || 'None'}
Wheelchair Access: ${booking.wheelchairAccess ? 'Yes' : 'No'}
Elderly Assistance: ${booking.elderlyAssistance ? 'Yes' : 'No'}
Medical Appointment: ${booking.medicalAppointment ? 'Yes' : 'No'}
Shopping Assistance: ${booking.shoppingAssistance ? 'Yes' : 'No'}

Quote Details:
Distance: ${booking.quoteDetails?.totalDistance || 'N/A'} km
Duration: ${booking.quoteDetails?.totalCompanionMinutes || 'N/A'} minutes
Total Fare: R${booking.quoteDetails?.totalCost || 'N/A'}

Status: ${booking.status}
Created: ${new Date(booking.created_at).toLocaleString()}
      `;
      alert(details);
    }
  };

  const renderDashboard = () => (
    <div className="admin-dashboard">
      <div className="dashboard-header">
        <h2>Dashboard Overview</h2>
        <div className="admin-time-filters">
          <button 
            className={adminTimeRange === 'today' ? 'time-btn active' : 'time-btn'}
            onClick={() => setAdminTimeRange('today')}
          >
            Today
          </button>
          <button 
            className={adminTimeRange === 'this_week' ? 'time-btn active' : 'time-btn'}
            onClick={() => setAdminTimeRange('this_week')}
          >
            This Week
          </button>
          <button 
            className={adminTimeRange === 'current_month' ? 'time-btn active' : 'time-btn'}
            onClick={() => setAdminTimeRange('current_month')}
          >
            This Month
          </button>
          <button 
            className={adminTimeRange === 'all_time' ? 'time-btn active' : 'time-btn'}
            onClick={() => setAdminTimeRange('all_time')}
          >
            All Time
          </button>
        </div>
      </div>
      
      <div className="stats-grid">
        <div className="stat-card">
          <h3>Total Bookings</h3>
          <p className="stat-number">{stats.totalBookings}</p>
        </div>
        <div className="stat-card">
          <h3>Completed Trips</h3>
          <p className="stat-number">{earnings.total_trips}</p>
        </div>
        <div className="stat-card">
          <h3>Active Drivers</h3>
          <p className="stat-number">{stats.activeDrivers}/{stats.totalDrivers}</p>
        </div>
        <div className="stat-card">
          <h3>Total Revenue</h3>
          <p className="stat-number">R{earnings.total_revenue?.toFixed(2) || '0.00'}</p>
        </div>
      </div>

      {/* Company Earnings Section */}
      <div className="earnings-breakdown">
        <h3>💰 {adminTimeRange === 'today' ? 'Today' : adminTimeRange === 'this_week' ? 'This Week' : adminTimeRange === 'current_month' ? 'This Month' : 'All Time'} Earnings Breakdown</h3>
        <div className="earnings-grid">
          <div className="earning-card-admin">
            <h4>Total Revenue</h4>
            <p className="earning-amount">R{earnings.total_revenue?.toFixed(2) || '0.00'}</p>
            <small>Total trip value</small>
          </div>
          <div className="earning-card-admin company">
            <h4>CompanionCab Commission (30%)</h4>
            <p className="earning-amount">R{earnings.company_commission?.toFixed(2) || '0.00'}</p>
            <small>Company earnings</small>
          </div>
          <div className="earning-card-admin">
            <h4>Driver Earnings (70%)</h4>
            <p className="earning-amount">R{earnings.driver_earnings?.toFixed(2) || '0.00'}</p>
            <small>Total paid to drivers</small>
          </div>
          <div className="earning-card-admin">
            <h4>Completed Trips</h4>
            <p className="earning-amount">{earnings.total_trips || 0}</p>
            <small>This month</small>
          </div>
        </div>
      </div>

      {/* Recent Activity Summary */}
      <div className="activity-summary">
        <h3>📊 Recent Activity</h3>
        <div className="activity-grid">
          <div className="activity-card">
            <h4>Pending Assignment</h4>
            <p className="activity-number">{bookings.filter(b => b.status === 'pending_assignment').length}</p>
            <small>Awaiting driver assignment</small>
          </div>
          <div className="activity-card">
            <h4>Driver Assigned</h4>
            <p className="activity-number">{bookings.filter(b => b.status === 'assigned').length}</p>
            <small>Waiting for driver confirmation</small>
          </div>
          <div className="activity-card">
            <h4>Confirmed</h4>
            <p className="activity-number">{bookings.filter(b => b.status === 'confirmed').length}</p>
            <small>Driver accepted trips</small>
          </div>
          <div className="activity-card">
            <h4>In Progress</h4>
            <p className="activity-number">{bookings.filter(b => b.status === 'in_progress').length}</p>
            <small>Currently active trips</small>
          </div>
        </div>
      </div>
      
      {pendingTrips.length > 0 && (
        <div className="pending-approvals">
          <h3>Pending Trip Approvals ({pendingTrips.length})</h3>
          <div className="trip-list">
            {pendingTrips.slice(0, 5).map(trip => (
              <div key={trip.id} className="trip-item">
                <div className="trip-info">
                  <strong>{trip.driver_name}</strong>
                  <p>{trip.pickup_address} → {trip.dropoff_address}</p>
                  <small>Fare: R{trip.fare} | Distance: {trip.distance}km</small>
                </div>
                <div className="trip-actions">
                  <button 
                    onClick={() => handleApproveTrip(trip.id)}
                    className="approve-btn"
                  >
                    Approve
                  </button>
                  <button 
                    onClick={() => handleRejectTrip(trip.id, 'Invalid trip data')}
                    className="reject-btn"
                  >
                    Reject
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Recent Bookings requiring attention */}
      {bookings.filter(b => b.status === 'pending_assignment' || b.status === 'assigned').length > 0 && (
        <div className="urgent-bookings">
          <h3>🚨 Bookings Requiring Attention</h3>
          <div className="urgent-list">
            {bookings
              .filter(b => b.status === 'pending_assignment' || b.status === 'assigned')
              .slice(0, 5)
              .map(booking => (
                <div key={booking.id} className="urgent-item">
                  <div className="urgent-info">
                    <h5>{booking.passengerName}</h5>
                    <p>{booking.pickupAddress} → {booking.destinationAddress}</p>
                    <p>Scheduled: {booking.scheduledDate} at {booking.scheduledTime}</p>
                    <span className={`urgent-status status-${booking.status}`}>
                      {booking.status === 'pending_assignment' ? 'Needs Driver Assignment' : 'Awaiting Driver Confirmation'}
                    </span>
                  </div>
                  <div className="urgent-actions">
                    {booking.status === 'pending_assignment' && (
                      <button 
                        onClick={() => handleAssignDriver(booking.id)}
                        className="assign-btn-small"
                      >
                        Assign Driver
                      </button>
                    )}
                    {booking.status === 'assigned' && (
                      <div className="assigned-info">
                        <small>Assigned to: {booking.driverInfo?.name}</small>
                        <small>Waiting for acceptance...</small>
                      </div>
                    )}
                  </div>
                </div>
              ))
            }
          </div>
        </div>
      )}
    </div>
  );

  const renderDriverManagement = () => (
    <div className="driver-management">
      <div className="section-header">
        <h2>Driver Management</h2>
        <button 
          onClick={() => setShowAddDriver(true)}
          className="add-driver-btn"
        >
          + Add New Driver
        </button>
      </div>
      
      {showAddDriver && (
        <div className="add-driver-form">
          <h3>Add New Driver</h3>
          <form onSubmit={handleCreateDriver}>
            <div className="form-row">
              <div className="input-group">
                <label htmlFor="driver-name">Full Name</label>
                <input
                  type="text"
                  id="driver-name"
                  name="driverName"
                  placeholder="Full Name"
                  value={newDriver.name}
                  onChange={(e) => setNewDriver({...newDriver, name: e.target.value})}
                  autocomplete="name"
                  required
                />
              </div>
              <div className="input-group">
                <label htmlFor="driver-email">Email</label>
                <input
                  type="email"
                  id="driver-email"
                  name="driverEmail"
                  placeholder="Email"
                  value={newDriver.email}
                  onChange={(e) => setNewDriver({...newDriver, email: e.target.value})}
                  autocomplete="email"
                  required
                />
              </div>
            </div>
            <div className="form-row">
              <div className="input-group">
                <label htmlFor="driver-phone">Phone Number</label>
                <input
                  type="tel"
                  id="driver-phone"
                  name="driverPhone"
                  placeholder="Phone Number"
                  value={newDriver.phone}
                  onChange={(e) => setNewDriver({...newDriver, phone: e.target.value})}
                  autocomplete="tel"
                  required
                />
              </div>
              <div className="input-group">
                <label htmlFor="driver-license">License Number</label>
                <input
                  type="text"
                  id="driver-license"
                  name="driverLicense"
                  placeholder="License Number"
                  value={newDriver.license}
                  onChange={(e) => setNewDriver({...newDriver, license: e.target.value})}
                  autocomplete="off"
                  required
                />
              </div>
            </div>
            <div className="form-row">
              <div className="input-group">
                <label htmlFor="vehicle-make">Vehicle Make</label>
                <input
                  type="text"
                  id="vehicle-make"
                  name="vehicleMake"
                  placeholder="Vehicle Make"
                  value={newDriver.vehicleMake}
                  onChange={(e) => setNewDriver({...newDriver, vehicleMake: e.target.value})}
                  autocomplete="off"
                  required
                />
              </div>
              <div className="input-group">
                <label htmlFor="vehicle-model">Vehicle Model</label>
                <input
                  type="text"
                  id="vehicle-model"
                  name="vehicleModel"
                  placeholder="Vehicle Model"
                  value={newDriver.vehicleModel}
                  onChange={(e) => setNewDriver({...newDriver, vehicleModel: e.target.value})}
                  autocomplete="off"
                  required
                />
              </div>
            </div>
            <div className="form-row">
              <div className="input-group">
                <label htmlFor="vehicle-year">Vehicle Year</label>
                <input
                  type="number"
                  id="vehicle-year"
                  name="vehicleYear"
                  placeholder="Vehicle Year"
                  value={newDriver.vehicleYear}
                  onChange={(e) => setNewDriver({...newDriver, vehicleYear: e.target.value})}
                  autocomplete="off"
                  min="1990"
                  max="2030"
                  required
                />
              </div>
              <div className="input-group">
                <label htmlFor="vehicle-color">Vehicle Color</label>
                <input
                  type="text"
                  id="vehicle-color"
                  name="vehicleColor"
                  placeholder="Vehicle Color"
                  value={newDriver.vehicleColor}
                  onChange={(e) => setNewDriver({...newDriver, vehicleColor: e.target.value})}
                  autocomplete="off"
                  required
                />
              </div>
            </div>
            <div className="form-row">
              <div className="input-group">
                <label htmlFor="license-plate">License Plate</label>
                <input
                  type="text"
                  id="license-plate"
                  name="licensePlate"
                  placeholder="License Plate"
                  value={newDriver.vehiclePlate}
                  onChange={(e) => setNewDriver({...newDriver, vehiclePlate: e.target.value})}
                  autocomplete="off"
                  required
                />
              </div>
            </div>
            <div className="form-actions">
              <button type="button" onClick={() => setShowAddDriver(false)}>Cancel</button>
              <button type="submit" disabled={loading}>
                {loading ? 'Creating...' : 'Create Driver'}
              </button>
            </div>
          </form>
        </div>
      )}
      
      <div className="drivers-list">
        <h3>Current Drivers ({drivers.length})</h3>
        <div className="drivers-grid">
          {drivers.map(driver => (
            <div key={driver.id} className="driver-card">
              <div className="driver-info">
                <h4>{driver.name}</h4>
                <p><strong>Email:</strong> {driver.email}</p>
                <p><strong>Phone:</strong> {driver.phone}</p>
                <p><strong>Vehicle:</strong> {driver.vehicle_make} {driver.vehicle_model} ({driver.vehicle_year})</p>
                <p><strong>Color:</strong> {driver.vehicle_color}</p>
                <p><strong>License:</strong> {driver.license_number}</p>
                <p><strong>Plate:</strong> {driver.vehicle_plate}</p>
              </div>
              <div className="driver-status">
                <span className={`status ${driver.is_available ? 'available' : 'busy'}`}>
                  {driver.is_available ? 'Available' : 'Busy'}
                </span>
                <span className={`status ${driver.is_active !== false ? 'available' : 'busy'}`} style={{ marginLeft: '0.25rem' }}>
                  {driver.is_active !== false ? 'Active' : 'Suspended'}
                </span>
                <span className="rating">⭐ {driver.rating || 5.0} ({driver.total_trips || 0} trips)</span>
              </div>
              <div style={{ marginTop: '0.75rem', display: 'flex', gap: '0.5rem' }}>
                {driver.is_active !== false ? (
                  <button
                    onClick={() => handleDeactivateDriver(driver.id, driver.name)}
                    data-testid={`deactivate-driver-${driver.id}`}
                    style={{ padding: '0.3rem 0.7rem', background: 'white', border: '1px solid #dc2626', color: '#dc2626', borderRadius: '6px', fontSize: '0.75rem', cursor: 'pointer' }}
                  >
                    Suspend
                  </button>
                ) : (
                  <button
                    onClick={() => handleActivateDriver(driver.id, driver.name)}
                    data-testid={`activate-driver-${driver.id}`}
                    style={{ padding: '0.3rem 0.7rem', background: '#22c55e', border: 'none', color: 'white', borderRadius: '6px', fontSize: '0.75rem', cursor: 'pointer' }}
                  >
                    Reactivate
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );

  const renderBookingManagement = () => (
    <div className="booking-management">
      <h2>Booking Management</h2>
      
      <div className="booking-filters">
        <button className={`filter-btn ${bookingFilter === 'all' ? 'active' : ''}`} onClick={() => setBookingFilter('all')}>
          All ({bookings.length})
        </button>
        <button className={`filter-btn ${bookingFilter === 'pending_assignment' ? 'active' : ''}`} onClick={() => setBookingFilter('pending_assignment')}>
          Pending ({bookings.filter(b => b.status === 'pending_assignment').length})
        </button>
        <button className={`filter-btn ${bookingFilter === 'assigned' ? 'active' : ''}`} onClick={() => setBookingFilter('assigned')}>
          Assigned ({bookings.filter(b => b.status === 'assigned').length})
        </button>
        <button className={`filter-btn ${bookingFilter === 'completed' ? 'active' : ''}`} onClick={() => setBookingFilter('completed')}>
          Completed ({bookings.filter(b => b.status === 'completed').length})
        </button>
        <button className={`filter-btn ${bookingFilter === 'cancelled' ? 'active' : ''}`} onClick={() => setBookingFilter('cancelled')}>
          Cancelled ({bookings.filter(b => b.status === 'cancelled').length})
        </button>
        {/* Sort Order */}
        <div style={{ marginLeft: 'auto', display: 'flex', gap: '0.25rem', alignItems: 'center' }}>
          <span style={{ fontSize: '0.8rem', color: '#666' }}>Sort:</span>
          <button className={`filter-btn ${bookingSortOrder === 'upcoming' ? 'active' : ''}`} onClick={() => setBookingSortOrder('upcoming')} data-testid="sort-upcoming">Upcoming First</button>
          <button className={`filter-btn ${bookingSortOrder === 'newest' ? 'active' : ''}`} onClick={() => setBookingSortOrder('newest')} data-testid="sort-newest">Newest First</button>
        </div>
      </div>
      
      <div className="bookings-list">
        {bookings
          .filter(booking => bookingFilter === 'all' || booking.status === bookingFilter)
          .sort((a, b) => {
            if (bookingSortOrder === 'upcoming') {
              const dateA = new Date(`${a.scheduledDate}T${a.scheduledTime || '00:00'}`);
              const dateB = new Date(`${b.scheduledDate}T${b.scheduledTime || '00:00'}`);
              return dateA - dateB;
            }
            return new Date(b.created_at) - new Date(a.created_at);
          })
          .map(booking => (
            <div key={booking.id} className="booking-item">
              <div className="booking-header">
                <h4>Booking #{booking.id.substring(0, 8)}</h4>
                <span className={`booking-status status-${booking.status}`}>
                  {booking.status.replace('_', ' ').toUpperCase()}
                </span>
              </div>
              
              <div className="booking-details">
                <div className="booking-info">
                  <div className="info-section">
                    <h5>Passenger Details</h5>
                    <p><strong>Name:</strong> {booking.passengerName}</p>
                    <p><strong>Phone:</strong> {booking.passengerPhone}</p>
                    <p><strong>Email:</strong> {booking.passengerEmail}</p>
                  </div>
                  
                  <div className="info-section">
                    <h5>Trip Details</h5>
                    <p><strong>From:</strong> {booking.pickupAddress}</p>
                    <p><strong>To:</strong> {booking.destinationAddress}</p>
                    <p><strong>Date:</strong> {booking.scheduledDate} at {booking.scheduledTime}</p>
                    <p><strong>Companion Time:</strong> {booking.companionMinutes} minutes</p>
                  </div>
                  
                  <div className="info-section">
                    <h5>Special Requirements</h5>
                    <div className="requirements">
                      {booking.wheelchairAccess && <span className="requirement">♿ Wheelchair Access</span>}
                      {booking.elderlyAssistance && <span className="requirement">👴 Elderly Assistance</span>}
                      {booking.medicalAppointment && <span className="requirement">🏥 Medical Appointment</span>}
                      {booking.shoppingAssistance && <span className="requirement">🛒 Shopping Assistance</span>}
                      {booking.specialRequirements && <span className="requirement">📝 {booking.specialRequirements}</span>}
                    </div>
                  </div>
                  
                  <div className="info-section">
                    <h5>Quote & Pricing</h5>
                    <p><strong>Distance:</strong> {booking.quoteDetails?.totalDistance || 'N/A'} km</p>
                    <p><strong>Duration:</strong> {booking.quoteDetails?.totalCompanionMinutes || 'N/A'} minutes</p>
                    <p>
                      <strong>Estimated Fare:</strong> R{booking.quoteDetails?.totalCost || booking.quoteDetails?.totalFare || 'N/A'}
                    </p>
                    {booking.actual_fare != null && (
                      <>
                        <p data-testid="actual-fare-admin">
                          <strong>Final Fare:</strong>{' '}
                          <span style={{ color: booking.fare_adjusted_by_admin ? '#A0306A' : '#22c55e', fontWeight: '600' }}>
                            R{booking.actual_fare}
                          </span>
                          {booking.fare_adjusted_by_admin && (
                            <small style={{ color: '#A0306A', marginLeft: '0.4rem' }}>(admin-adjusted)</small>
                          )}
                        </p>
                        <p><strong>Driver Earnings (70%):</strong> R{booking.driver_earnings?.toFixed(2) || 'N/A'}</p>
                        <p><strong>Company (30%):</strong> R{booking.company_commission?.toFixed(2) || 'N/A'}</p>
                      </>
                    )}
                    {booking.status === 'completed' && (
                      <button
                        data-testid={`adjust-fare-btn-${booking.id}`}
                        onClick={async () => {
                          const current = booking.actual_fare ?? booking.quoteDetails?.totalCost ?? booking.quoteDetails?.totalFare ?? '';
                          const newFare = window.prompt(`Adjust final fare for this booking:\nCurrent: R${current}\n\nEnter new fare amount:`, current);
                          if (!newFare || isNaN(parseFloat(newFare))) return;
                          const reason = window.prompt('Reason for adjustment (optional):') || '';
                          const res = await fetch(`${BACKEND_URL}/api/admin/bookings/${booking.id}/adjust-fare`, {
                            method: 'PUT',
                            headers: { 'Content-Type': 'application/json' },
                            body: JSON.stringify({ new_fare: parseFloat(newFare), reason })
                          });
                          if (res.ok) { const d = await res.json(); alert(d.message); loadAdminData(); }
                          else { const d = await res.json(); alert(d.detail || 'Failed to adjust fare'); }
                        }}
                        style={{ marginTop: '0.5rem', padding: '0.3rem 0.7rem', background: 'white', border: '1px solid #A0306A', color: '#A0306A', borderRadius: '6px', fontSize: '0.75rem', cursor: 'pointer' }}
                      >
                        Adjust Fare
                      </button>
                    )}
                  </div>
                  
                  {booking.driverInfo && (
                    <div className="info-section">
                      <h5>Assigned Driver</h5>
                      <p><strong>Name:</strong> {booking.driverInfo.name}</p>
                      <p><strong>Phone:</strong> {booking.driverInfo.phone}</p>
                      <p><strong>Vehicle:</strong> {booking.driverInfo.vehicle}</p>
                    </div>
                  )}
                </div>
                
                <div className="booking-actions">
                  <small className="booking-date">
                    Created: {new Date(booking.created_at).toLocaleDateString('en-ZA')} at {new Date(booking.created_at).toLocaleTimeString('en-ZA', {hour: '2-digit', minute: '2-digit'})}
                  </small>
                  
                  <div className="action-buttons">
                    {booking.status === 'pending_assignment' && (
                      <>
                        <button 
                          className="action-btn assign-btn"
                          onClick={() => handleAssignDriver(booking.id)}
                        >
                          Assign Driver
                        </button>
                        <button
                          className="action-btn"
                          onClick={() => handleSendNoDriverMessage(booking.id, booking.passengerName)}
                          data-testid={`no-driver-btn-${booking.id}`}
                          style={{ background: '#fff3cd', border: '1px solid #ffc107', color: '#856404' }}
                        >
                          No Driver Available
                        </button>
                        <button 
                          className="action-btn cancel-btn"
                          onClick={() => handleCancelBooking(booking.id)}
                        >
                          Cancel Booking
                        </button>
                      </>
                    )}
                    {booking.status === 'assigned' && (
                      <>
                        <button 
                          className="action-btn start-btn"
                          onClick={() => handleStartTrip(booking.id)}
                        >
                          Start Trip
                        </button>
                        <button 
                          className="action-btn reassign-btn"
                          onClick={() => handleReassignDriver(booking.id)}
                        >
                          Reassign Driver
                        </button>
                      </>
                    )}
                    {booking.status === 'in_progress' && (
                      <button 
                        className="action-btn complete-btn"
                        onClick={() => handleCompleteTrip(booking.id)}
                      >
                        Complete Trip
                      </button>
                    )}
                    <button 
                      className="action-btn view-btn"
                      onClick={() => handleViewBookingDetails(booking.id)}
                    >
                      View Details
                    </button>
                  </div>
                </div>
              </div>
            </div>
          ))
        }
        
        {bookings.filter(booking => bookingFilter === 'all' || booking.status === bookingFilter).length === 0 && (
          <div className="no-bookings">
            <p>No bookings found for the selected filter.</p>
          </div>
        )}
      </div>
    </div>
  );

  const renderDriverApplications = () => (
    <div className="driver-management">
      <h2>Driver Applications</h2>
      <p style={{ color: '#666', marginBottom: '1.5rem', fontSize: '0.9rem' }}>Review and act on driver applications submitted through the app.</p>
      {driverApplications.length === 0 ? (
        <div className="no-trips"><p>No driver applications found.</p></div>
      ) : (
        <div className="drivers-grid">
          {driverApplications.map(app => (
            <div key={app.id} className="driver-card" style={{ borderLeft: `4px solid ${app.status === 'pending_review' ? '#f59e0b' : app.status === 'approved' ? '#22c55e' : '#ef4444'}` }}>
              <div className="driver-info">
                <h4>{app.fullName}</h4>
                <p><strong>Email:</strong> {app.email}</p>
                <p><strong>Phone:</strong> {app.phone}</p>
                <p><strong>City:</strong> {app.city}</p>
                {app.licenseNumber && <p><strong>License:</strong> {app.licenseNumber}</p>}
                {app.vehicleMake && <p><strong>Vehicle:</strong> {app.vehicleMake} {app.vehicleModel} ({app.vehicleYear})</p>}
                {app.yearsExperience && <p><strong>Experience:</strong> {app.yearsExperience} years</p>}
                <p style={{ marginTop: '0.5rem' }}><strong>Submitted:</strong> {app.created_at ? new Date(app.created_at).toLocaleDateString('en-ZA') : 'N/A'}</p>
              </div>
              <div className="driver-status">
                <span className={`status ${app.status === 'approved' ? 'available' : app.status === 'rejected' ? 'busy' : ''}`} style={{ background: app.status === 'pending_review' ? '#fef3c7' : undefined, color: app.status === 'pending_review' ? '#92400e' : undefined }}>
                  {app.status === 'pending_review' ? 'Pending Review' : app.status.charAt(0).toUpperCase() + app.status.slice(1)}
                </span>
              </div>
              {app.status === 'pending_review' && (
                <div style={{ marginTop: '0.75rem', display: 'flex', gap: '0.5rem' }}>
                  <button
                    onClick={() => handleApplicationAction(app.id, 'approve')}
                    data-testid={`approve-app-${app.id}`}
                    style={{ flex: 1, padding: '0.4rem', background: '#22c55e', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '0.8rem' }}
                  >
                    Approve
                  </button>
                  <button
                    onClick={() => handleApplicationAction(app.id, 'reject')}
                    data-testid={`reject-app-${app.id}`}
                    style={{ flex: 1, padding: '0.4rem', background: 'white', border: '1px solid #ef4444', color: '#ef4444', borderRadius: '6px', cursor: 'pointer', fontSize: '0.8rem' }}
                  >
                    Reject
                  </button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );

  return (
    <div className="admin-view">
      <div className="admin-header">
        <h1>Admin Console</h1>
        <div className="admin-nav">
          <button className={activeTab === 'dashboard' ? 'active' : ''} onClick={() => setActiveTab('dashboard')}>Dashboard</button>
          <button className={activeTab === 'drivers' ? 'active' : ''} onClick={() => setActiveTab('drivers')}>Drivers</button>
          <button className={activeTab === 'bookings' ? 'active' : ''} onClick={() => setActiveTab('bookings')}>Bookings</button>
          <button className={activeTab === 'applications' ? 'active' : ''} onClick={() => { setActiveTab('applications'); loadDriverApplications(); }} style={{ position: 'relative' }}>
            Applications
          </button>
          {/* Notification Bell */}
          <div ref={notifRef} style={{ position: 'relative', marginLeft: '0.5rem' }}>
            <button
              data-testid="notification-bell"
              onClick={() => setShowNotifications(!showNotifications)}
              style={{ position: 'relative', background: showNotifications ? '#F7D6E8' : 'transparent', border: '1px solid #e2e8f0', borderRadius: '8px', padding: '0.4rem 0.6rem', cursor: 'pointer', fontSize: '1rem' }}
            >
              🔔
              {unreadCount > 0 && (
                <span data-testid="notif-badge" style={{ position: 'absolute', top: '-6px', right: '-6px', background: '#A0306A', color: 'white', borderRadius: '50%', fontSize: '0.65rem', width: '18px', height: '18px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: '700' }}>
                  {unreadCount > 9 ? '9+' : unreadCount}
                </span>
              )}
            </button>
            {showNotifications && (
              <div style={{ position: 'absolute', right: 0, top: '110%', width: '340px', background: 'white', border: '1px solid #e2e8f0', borderRadius: '12px', boxShadow: '0 8px 24px rgba(0,0,0,0.12)', zIndex: 1000, maxHeight: '400px', overflow: 'hidden', display: 'flex', flexDirection: 'column' }} data-testid="notifications-dropdown">
                <div style={{ padding: '0.75rem 1rem', borderBottom: '1px solid #f0f0f0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <strong style={{ fontSize: '0.9rem' }}>Notifications</strong>
                  {unreadCount > 0 && <button onClick={markAllRead} style={{ fontSize: '0.75rem', color: '#A0306A', background: 'none', border: 'none', cursor: 'pointer' }}>Mark all read</button>}
                </div>
                <div style={{ overflowY: 'auto', flex: 1 }}>
                  {notifications.length === 0 ? (
                    <p style={{ padding: '1rem', color: '#999', fontSize: '0.85rem', textAlign: 'center' }}>No notifications</p>
                  ) : notifications.map(n => (
                    <div key={n.id} onClick={() => markNotificationRead(n.id)} style={{ padding: '0.75rem 1rem', borderBottom: '1px solid #f8f8f8', background: n.is_read ? 'white' : '#fff5fa', cursor: 'pointer', transition: 'background 0.2s' }}>
                      <div style={{ fontWeight: n.is_read ? '400' : '600', fontSize: '0.85rem', color: '#333' }}>{n.title}</div>
                      <div style={{ fontSize: '0.8rem', color: '#666', marginTop: '0.2rem' }}>{n.message}</div>
                      <div style={{ fontSize: '0.7rem', color: '#999', marginTop: '0.2rem' }}>{n.created_at ? new Date(n.created_at).toLocaleString() : ''}</div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
      
      <div className="admin-content">
        {activeTab === 'dashboard' && renderDashboard()}
        {activeTab === 'drivers' && renderDriverManagement()}
        {activeTab === 'bookings' && renderBookingManagement()}
        {activeTab === 'applications' && renderDriverApplications()}
      </div>
      
      {/* User Profile Modal */}
      {showProfile && (
        <UserProfile onClose={() => setShowProfile(false)} />
      )}
    </div>
  );
};

export default AdminView;